import { RNMessageType } from '@/types/native-bridge';

/**
 * Native Message Handler
 * Handles deduplication and routing of messages from React Native
 */

type MessageHandler = (payload: unknown) => void | Promise<void>;
type MessageID = string;

class NativeMessageHandler {
  private handlers: Map<RNMessageType, Set<MessageHandler>> = new Map();
  private processedMessages: Set<MessageID> = new Set();
  private readonly MAX_PROCESSED_MESSAGES = 100;

  constructor() {
    this.setupGlobalListener();
  }

  private setupGlobalListener() {
    document.addEventListener('RN_MESSAGE', this.handleMessage.bind(this));
  }

  private async handleMessage(event: Event) {
    const message = event as MessageEvent<{
      type?: RNMessageType;
      payload?: unknown;
      messageId?: string;
    }>;

    if (!message?.data?.type) return;

    const { type, payload, messageId } = message.data;

    // Message deduplication
    if (messageId) {
      if (this.processedMessages.has(messageId)) {
        console.warn('[NativeMessage] Duplicate message ignored:', messageId);
        return;
      }
      this.addProcessedMessage(messageId);
    }

    // Route to handlers
    const handlers = this.handlers.get(type as RNMessageType);
    if (!handlers || handlers.size === 0) {
      console.log('[NativeMessage] No handlers for:', type);
      return;
    }

    // Execute all handlers for this message type
    for (const handler of handlers) {
      try {
        await handler(payload);
      } catch (error) {
        console.error('[NativeMessage] Handler error:', error);
      }
    }
  }

  private addProcessedMessage(messageId: MessageID) {
    this.processedMessages.add(messageId);

    // Limit memory usage - keep only recent messages
    if (this.processedMessages.size > this.MAX_PROCESSED_MESSAGES) {
      const firstItem = this.processedMessages.values().next().value;
      if (firstItem) {
        this.processedMessages.delete(firstItem);
      }
    }
  }

  /**
   * Subscribe to a message type
   */
  subscribe(type: RNMessageType, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(type)?.delete(handler);
    };
  }

  /**
   * Clear all processed message IDs (useful for testing)
   */
  clearProcessedMessages() {
    this.processedMessages.clear();
  }
}

// Singleton instance
export const nativeMessageHandler = new NativeMessageHandler();
